import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  limit as firestoreLimit,
} from 'firebase/firestore';
import type { ProductDocument, ProductVariantDocument } from '@/types/manage.types';
import { IProductRepository } from '../../domain/repositories/IProductRepository';
import { getFirebaseDb, PRODUCT_COLLECTION, PRODUCT_VARIANTS_COLLECTION } from '../../../lib/firebase';

/**
 * Firebase implementation of the Product Repository
 * Handles all product and variant data operations with Firebase Firestore
 */
export class FirebaseProductRepository implements IProductRepository {
  // Product operations
  async findProductById(productId: string): Promise<ProductDocument | null> {
    try {
      const db = await getFirebaseDb();
      const docRef = doc(db, PRODUCT_COLLECTION, productId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      return { id: docSnap.id, ...docSnap.data() } as ProductDocument;
    } catch (error) {
      console.error('Error finding product by ID:', error);
      throw error;
    }
  }

  async findAllProducts(limit?: number): Promise<ProductDocument[]> {
    try {
      const db = await getFirebaseDb();
      let q = query(collection(db, PRODUCT_COLLECTION), orderBy('name'));

      if (limit) {
        q = query(q, firestoreLimit(limit));
      }

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(
        (doc) =>
          ({
            id: doc.id,
            ...doc.data(),
          }) as ProductDocument,
      );
    } catch (error) {
      console.error('Error finding all products:', error);
      throw error;
    }
  }

  async findProductsByCategory(category: string): Promise<ProductDocument[]> {
    try {
      const db = await getFirebaseDb();
      const q = query(collection(db, PRODUCT_COLLECTION), where('category', '==', category), orderBy('name'));

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(
        (doc) =>
          ({
            id: doc.id,
            ...doc.data(),
          }) as ProductDocument,
      );
    } catch (error) {
      console.error('Error finding products by category:', error);
      throw error;
    }
  }

  async saveProduct(product: ProductDocument): Promise<void> {
    try {
      const db = await getFirebaseDb();
      const docRef = doc(collection(db, PRODUCT_COLLECTION), product.id);
      await setDoc(docRef, product);
    } catch (error) {
      console.error('Error saving product:', error);
      throw error;
    }
  }

  async deleteProduct(productId: string): Promise<void> {
    try {
      const db = await getFirebaseDb();
      const docRef = doc(db, PRODUCT_COLLECTION, productId);
      await deleteDoc(docRef);
    } catch (error) {
      console.error('Error deleting product:', error);
      throw error;
    }
  }

  // Product variant operations
  async findVariantById(variantId: string): Promise<ProductVariantDocument | null> {
    try {
      const db = await getFirebaseDb();
      const docRef = doc(db, PRODUCT_VARIANTS_COLLECTION, variantId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      return { id: docSnap.id, ...docSnap.data() } as ProductVariantDocument;
    } catch (error) {
      console.error('Error finding variant by ID:', error);
      throw error;
    }
  }

  async findAllVariants(limit?: number): Promise<ProductVariantDocument[]> {
    try {
      const db = await getFirebaseDb();
      let q = query(collection(db, PRODUCT_VARIANTS_COLLECTION), orderBy('created_at'));

      if (limit) {
        q = query(q, firestoreLimit(limit));
      }

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(
        (doc) =>
          ({
            id: doc.id,
            ...doc.data(),
          }) as ProductVariantDocument,
      );
    } catch (error) {
      console.error('Error finding all variants:', error);
      throw error;
    }
  }

  async findVariantsByProductId(productId: string): Promise<ProductVariantDocument[]> {
    try {
      const db = await getFirebaseDb();
      const q = query(
        collection(db, PRODUCT_VARIANTS_COLLECTION),
        where('product_id', '==', productId),
        orderBy('is_default', 'desc'),
        orderBy('created_at'),
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(
        (doc) =>
          ({
            id: doc.id,
            ...doc.data(),
          }) as ProductVariantDocument,
      );
    } catch (error) {
      console.error('Error finding variants by product ID:', error);
      throw error;
    }
  }

  async saveVariant(variant: ProductVariantDocument): Promise<void> {
    try {
      const db = await getFirebaseDb();
      const docRef = doc(collection(db, PRODUCT_VARIANTS_COLLECTION), variant.id);
      await setDoc(docRef, variant);
    } catch (error) {
      console.error('Error saving variant:', error);
      throw error;
    }
  }

  async deleteVariant(variantId: string): Promise<void> {
    try {
      const db = await getFirebaseDb();
      const docRef = doc(db, PRODUCT_VARIANTS_COLLECTION, variantId);
      await deleteDoc(docRef);
    } catch (error) {
      console.error('Error deleting variant:', error);
      throw error;
    }
  }

  async updateVariantPrice(variantId: string, price: number): Promise<void> {
    try {
      const db = await getFirebaseDb();
      const docRef = doc(db, PRODUCT_VARIANTS_COLLECTION, variantId);
      await updateDoc(docRef, {
        price: price,
        updated_at: Date.now(),
      });
    } catch (error) {
      console.error('Error updating variant price:', error);
      throw error;
    }
  }

  // Subscription operations
  subscribeToProducts(callback: (products: ProductDocument[]) => void, limit?: number): () => void {
    let unsubscribe: (() => void) | null = null;

    (async () => {
      try {
        const db = await getFirebaseDb();
        let q = query(collection(db, PRODUCT_COLLECTION), orderBy('name'));

        if (limit) {
          q = query(q, firestoreLimit(limit));
        }

        unsubscribe = onSnapshot(
          q,
          (snapshot) => {
            const products = snapshot.docs.map(
              (doc) =>
                ({
                  id: doc.id,
                  ...doc.data(),
                }) as ProductDocument,
            );
            callback(products);
          },
          (error) => {
            console.error('Error in products subscription:', error);
          },
        );
      } catch (error) {
        console.error('Error setting up products subscription:', error);
      }
    })();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }

  subscribeToVariants(callback: (variants: ProductVariantDocument[]) => void, limit?: number): () => void {
    let unsubscribe: (() => void) | null = null;

    (async () => {
      try {
        const db = await getFirebaseDb();
        let q = query(collection(db, PRODUCT_VARIANTS_COLLECTION), orderBy('created_at'));

        if (limit) {
          q = query(q, firestoreLimit(limit));
        }

        unsubscribe = onSnapshot(
          q,
          (snapshot) => {
            const variants = snapshot.docs.map(
              (doc) =>
                ({
                  id: doc.id,
                  ...doc.data(),
                }) as ProductVariantDocument,
            );
            callback(variants);
          },
          (error) => {
            console.error('Error in variants subscription:', error);
          },
        );
      } catch (error) {
        console.error('Error setting up variants subscription:', error);
      }
    })();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }

  // Utility operations
  async getProductCategories(): Promise<string[]> {
    try {
      const products = await this.findAllProducts();
      const categories = new Set(products.map((product) => product.category));
      return Array.from(categories).sort();
    } catch (error) {
      console.error('Error getting product categories:', error);
      throw error;
    }
  }

  async searchProducts(query: string): Promise<ProductDocument[]> {
    try {
      const allProducts = await this.findAllProducts();
      const lowercaseQuery = query.toLowerCase();

      return allProducts.filter(
        (product) =>
          product.name.toLowerCase().includes(lowercaseQuery) ||
          product.description?.toLowerCase().includes(lowercaseQuery) ||
          (product.ingredients &&
            product.ingredients.some((ingredient) => ingredient.toLowerCase().includes(lowercaseQuery))),
      );
    } catch (error) {
      console.error('Error searching products:', error);
      throw error;
    }
  }
}
